import React, { useState, useEffect, useCallback } from 'react';
import { CreditCard, Loader2, CheckCircle, Crown, Zap, Star, Gem, Shield, Users, Lock, ArrowUpRight, Sparkles, Clock, AlertTriangle, RefreshCw, Leaf, Radar, Plus, Package, Minus, XCircle, ShoppingCart, Receipt, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../config/apiBase';


const PLAN_META = {
    free: { icon: Star, color: '#6B7280', gradient: 'linear-gradient(135deg, rgba(107,114,128,0.10) 0%, rgba(107,114,128,0.02) 100%)' },
    lite: { icon: Leaf, color: '#10B981', gradient: 'linear-gradient(135deg, rgba(16,185,129,0.10) 0%, rgba(16,185,129,0.02) 100%)' },
    start: { icon: Zap, color: '#F59E0B', gradient: 'linear-gradient(135deg, rgba(245,158,11,0.10) 0%, rgba(245,158,11,0.02) 100%)' },
    gestão: { icon: Crown, color: '#3B82F6', gradient: 'linear-gradient(135deg, rgba(59,130,246,0.10) 0%, rgba(59,130,246,0.02) 100%)' },
    pro: { icon: Gem, color: '#8B5CF6', gradient: 'linear-gradient(135deg, rgba(139,92,246,0.10) 0%, rgba(139,92,246,0.02) 100%)' },
    enterprise: { icon: Sparkles, color: 'var(--sv-gold-primary)', gradient: 'linear-gradient(135deg, rgba(var(--sv-accent-rgb), 0.10) 0%, rgba(var(--sv-accent-rgb), 0.02) 100%)' },
};

const PLAN_ORDER = ['free', 'lite', 'start', 'gestão', 'pro', 'enterprise'];
const RADAR_LABELS = { 0: 'Sem acesso', 1: 'Muito baixa', 2: 'Baixa', 3: 'Media', 4: 'Alta', 5: 'Maxima' };
const RADAR_COLORS = { 0: '#6B7280', 1: '#9CA3AF', 2: '#F59E0B', 3: '#3B82F6', 4: '#8B5CF6', 5: '#3BEA86' };

const AVAILABLE_ADDONS_META = {
    suporte_prioritario: { name: 'Suporte Prioritario', icon: Shield, color: '#3B82F6' },
    radar_ofertas_addon: { name: 'Radar de Ofertas', icon: Radar, color: '#8B5CF6' },
    usuarios_extras: { name: 'Usuarios Extras', icon: Users, color: '#F59E0B' },
};

function LimitBar({ label, current, max, color }) {
    const pct = max > 0 ? Math.min((current / max) * 100, 100) : 0;
    const isNearLimit = pct >= 80;
    return (
        <div className="p-3 rounded-lg" style={{ background: 'var(--surface)' }}>
            <div className="flex items-center justify-between mb-1.5">
                <p className="text-[10px]" style={{ color: 'var(--muted)' }}>{label}</p>
                <p className="text-[10px] font-bold" style={{ color: isNearLimit ? '#EF4444' : 'var(--text)' }}>{current} / {max >= 9999 ? 'Ilimitado' : max}</p>
            </div>
            <div className="w-full h-1.5 rounded-full" style={{ background: 'var(--surface-2)' }}>
                <div className="h-full rounded-full transition-all" style={{ width: `${max >= 9999 ? 5 : pct}%`, background: isNearLimit ? '#EF4444' : color }} />
            </div>
        </div>
    );
}

export default function EmpresaAssinaturaPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showCompare, setShowCompare] = useState(false);
    const [checkingPayment, setCheckingPayment] = useState(false);
    const [upgradingPlan, setUpgradingPlan] = useState(null);
    const [buyingAddon, setBuyingAddon] = useState(null);
    const [cancellingAddon, setCancellingAddon] = useState(null);
    const [addonQuantities, setAddonQuantities] = useState({});
    const [txHistory, setTxHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [loadingHistory, setLoadingHistory] = useState(false);

    const token = localStorage.getItem('token') || sessionStorage.getItem('token');

    const fetchPlanInfo = useCallback(async () => {
        if (!token) return;
        try {
            const r = await fetch(`${API}/empresa/plano-info`, { headers: { 'Authorization': `Bearer ${token}` } });
            const d = await r.json();
            setData(d);
        } catch {} finally { setLoading(false); }
    }, [token]);

    useEffect(() => { fetchPlanInfo(); }, [fetchPlanInfo]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const sessionId = params.get('session_id');
        const addonSessionId = params.get('addon_session_id');
        const cancelled = params.get('cancelled');
        if (cancelled) { toast.info('Pagamento cancelado'); window.history.replaceState({}, '', window.location.pathname); }
        if (sessionId) { setCheckingPayment(true); pollPaymentStatus(sessionId, 0); }
        if (addonSessionId) { setCheckingPayment(true); pollAddonPaymentStatus(addonSessionId, 0); }
    }, []);

    const pollPaymentStatus = async (sessionId, attempts) => {
        if (attempts >= 6) { setCheckingPayment(false); toast.error('Tempo esgotado verificando pagamento.'); window.history.replaceState({}, '', window.location.pathname); return; }
        try {
            const res = await fetch(`${API}/payments/status/${sessionId}`, { headers: { 'Authorization': `Bearer ${token}` } });
            const d = await res.json();
            if (d.payment_status === 'paid') { setCheckingPayment(false); toast.success(`Plano ${d.plan || ''} ativado!`); window.history.replaceState({}, '', window.location.pathname); fetchPlanInfo(); return; }
            if (d.status === 'expired') { setCheckingPayment(false); toast.error('Sessao expirada.'); window.history.replaceState({}, '', window.location.pathname); return; }
            setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
        } catch { setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000); }
    };

    const handleUpgrade = async (planSlug) => {
        setUpgradingPlan(planSlug);
        try {
            const res = await fetch(`${API}/payments/create-checkout`, {
                method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ plan: planSlug, origin_url: window.location.origin }),
            });
            const d = await res.json();
            if (d.url) window.location.href = d.url;
            else toast.error(d.detail || 'Erro ao criar sessao');
        } catch { toast.error('Erro de conexão'); } finally { setUpgradingPlan(null); }
    };

    const pollAddonPaymentStatus = async (sessionId, attempts) => {
        if (attempts >= 6) { setCheckingPayment(false); toast.error('Tempo esgotado verificando pagamento do add-on.'); window.history.replaceState({}, '', window.location.pathname); return; }
        try {
            const res = await fetch(`${API}/payments/addon-status/${sessionId}`, { headers: { 'Authorization': `Bearer ${token}` } });
            const d = await res.json();
            if (d.payment_status === 'paid') { setCheckingPayment(false); toast.success(`Add-on ${d.addon_key || ''} ativado!`); window.history.replaceState({}, '', window.location.pathname); fetchPlanInfo(); return; }
            if (d.status === 'expired') { setCheckingPayment(false); toast.error('Sessao expirada.'); window.history.replaceState({}, '', window.location.pathname); return; }
            setTimeout(() => pollAddonPaymentStatus(sessionId, attempts + 1), 2000);
        } catch { setTimeout(() => pollAddonPaymentStatus(sessionId, attempts + 1), 2000); }
    };

    const handleBuyAddon = async (addonKey) => {
        setBuyingAddon(addonKey);
        const quantity = addonKey === 'usuarios_extras' ? (addonQuantities[addonKey] || 1) : 1;
        try {
            const res = await fetch(`${API}/payments/create-addon-checkout`, {
                method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ addon_key: addonKey, quantity, origin_url: window.location.origin }),
            });
            const d = await res.json();
            if (d.url) window.location.href = d.url;
            else toast.error(d.detail || 'Erro ao criar sessao do add-on');
        } catch { toast.error('Erro de conexão'); } finally { setBuyingAddon(null); }
    };

    const handleCancelAddon = async (addonKey) => {
        if (!window.confirm(`Tem certeza que deseja cancelar o add-on "${AVAILABLE_ADDONS_META[addonKey]?.name || addonKey}"?`)) return;
        setCancellingAddon(addonKey);
        try {
            const res = await fetch(`${API}/payments/cancel-addon`, {
                method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ addon_key: addonKey }),
            });
            const d = await res.json();
            if (d.ok) { toast.success(d.message || 'Add-on cancelado'); fetchPlanInfo(); }
            else toast.error(d.detail || 'Erro ao cancelar');
        } catch { toast.error('Erro de conexão'); } finally { setCancellingAddon(null); }
    };

    const updateAddonQty = (addonKey, delta) => {
        setAddonQuantities(prev => ({ ...prev, [addonKey]: Math.max(1, Math.min(100, (prev[addonKey] || 1) + delta)) }));
    };

    const fetchTxHistory = async () => {
        if (txHistory.length > 0) { setShowHistory(!showHistory); return; }
        setLoadingHistory(true);
        try {
            const r = await fetch(`${API}/payments/my-transactions`, { headers: { 'Authorization': `Bearer ${token}` } });
            if (r.ok) { const d = await r.json(); setTxHistory(d.transactions || []); setShowHistory(true); }
        } catch {} finally { setLoadingHistory(false); }
    };

    if (loading || !data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--sv-gold-primary)' }} /></div>;
    if (checkingPayment) return (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
            <Loader2 className="animate-spin" size={32} style={{ color: 'var(--sv-gold-primary)' }} />
            <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>Verificando pagamento...</p>
        </div>
    );

    const currentPlan = data.plan || 'free';
    const currentMeta = PLAN_META[currentPlan] || PLAN_META.free;
    const CurrentIcon = currentMeta.icon;
    const planDays = data.plan_expires ? Math.ceil((new Date(data.plan_expires) - new Date()) / 86400000) : null;
    const allPlans = data.all_plans || [];
    const currentIdx = PLAN_ORDER.indexOf(currentPlan);
    const limits = data.limits || {};
    const isTrial = data.trial_active;
    const autoRenew = data.auto_renew !== false;
    const radarPriority = data.radar_priority || 0;
    const activeAddons = data.addons || [];
    const addonDetails = data.addon_details || [];
    const availableAddons = data.available_addons || [];

    const toggleAutoRenew = async () => {
        try {
            const r = await fetch(`${API}/ponto/empresa/auto-renew`, {
                method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ auto_renew: !autoRenew }),
            });
            if (r.ok) { toast.success(autoRenew ? 'Renovação automática desativada' : 'Renovação automática ativada'); fetchPlanInfo(); }
        } catch { toast.error('Erro ao atualizar'); }
    };

    return (
        <div className="space-y-5" data-testid="empresa-assinatura">
            {/* Trial banner */}
            {isTrial && planDays !== null && planDays > 0 && (
                <div className="rounded-xl p-3 flex items-center gap-3" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.20)' }}>
                    <AlertTriangle size={16} style={{ color: '#F59E0B' }} />
                    <p className="text-xs flex-1" style={{ color: '#F59E0B' }}>
                        <strong>Trial ativo!</strong> {planDays} dia(s) restante(s) no plano {data.plan_name}. Faca upgrade para não perder acesso.
                    </p>
                    <button onClick={() => setShowCompare(true)} className="px-3 py-1.5 rounded-lg text-xs font-semibold shrink-0" style={{ background: '#F59E0B', color: '#0A0A0A' }} data-testid="trial-upgrade-btn">Assinar agora</button>
                </div>
            )}

            {/* Current Plan Hero */}
            <div className="rounded-2xl p-6 relative overflow-hidden" style={{ background: currentMeta.gradient, border: `1px solid ${currentMeta.color}25` }}>
                <div className="absolute top-0 right-0 w-32 h-32 rounded-full" style={{ background: `${currentMeta.color}06`, transform: 'translate(30%, -30%)' }} />
                <div className="flex items-start gap-4 mb-5">
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0" style={{ background: `${currentMeta.color}15`, border: `1px solid ${currentMeta.color}25` }}>
                        <CurrentIcon size={24} style={{ color: currentMeta.color }} />
                    </div>
                    <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                            <h2 className="text-xl font-bold capitalize" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Plano {data.plan_name || currentPlan}</h2>
                            {isTrial && <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ background: 'rgba(245,158,11,0.15)', color: '#F59E0B' }}>TRIAL</span>}
                        </div>
                        <div className="flex items-center gap-3 text-xs" style={{ color: 'var(--muted)' }}>
                            <span style={{ color: (data.plan_status === 'active' || data.plan_status === 'trial') ? '#10B981' : data.plan_status === 'expired' ? '#EF4444' : '#F59E0B' }}>
                                {data.plan_status === 'active' ? 'Ativo' : data.plan_status === 'trial' ? 'Trial' : data.plan_status === 'expired' ? 'Expirado' : 'Pendente'}
                            </span>
                            {planDays !== null && <span className="flex items-center gap-1"><Clock size={10} />{planDays > 0 ? `${planDays} dias restantes` : 'Expirado'}</span>}
                            {data.price > 0 && <span className="font-semibold" style={{ color: currentMeta.color }}>R$ {data.price.toFixed(2)}/mes</span>}
                            {data.price === 0 && currentPlan !== 'enterprise' && <span style={{ color: '#10B981' }}>Gratuito</span>}
                        </div>
                    </div>
                </div>

                {/* Limits */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <LimitBar label="Colaboradores" current={limits.colaboradores?.current || 0} max={limits.colaboradores?.max || 1} color={currentMeta.color} />
                    <LimitBar label="Gestores" current={limits.gestores?.current || 0} max={limits.gestores?.max || 0} color={currentMeta.color} />
                    <LimitBar label="Lançamentos financeiros (mes)" current={limits.financeiro?.current || 0} max={limits.financeiro?.max || 10} color={currentMeta.color} />
                </div>

                {/* Radar Priority */}
                <div className="mt-3 p-3 rounded-lg flex items-center gap-3" style={{ background: 'var(--surface)' }}>
                    <Radar size={14} style={{ color: RADAR_COLORS[radarPriority] || '#6B7280' }} />
                    <div className="flex-1">
                        <p className="text-[10px]" style={{ color: 'var(--muted)' }}>Prioridade no Radar de Ofertas</p>
                        <p className="text-xs font-bold" style={{ color: RADAR_COLORS[radarPriority] || '#6B7280' }}>{RADAR_LABELS[radarPriority] || 'Sem acesso'}</p>
                    </div>
                    {radarPriority < 3 && currentPlan !== 'enterprise' && (
                        <span className="text-[9px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(var(--sv-accent-rgb), 0.10)', color: 'var(--sv-gold-primary)' }}>Upgrade para mais prioridade</span>
                    )}
                </div>

                {/* Active Modules */}
                {data.modules && data.modules.length > 0 && (
                    <div className="mt-4">
                        <p className="text-[10px] mb-2 font-semibold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>Módulos ativos ({data.modules.length})</p>
                        <div className="flex gap-1.5 flex-wrap">
                            {data.modules.slice(0, 14).map(m => (
                                <span key={m} className="text-[10px] px-2 py-0.5 rounded-full capitalize flex items-center gap-1" style={{ background: `${currentMeta.color}10`, color: currentMeta.color }}>
                                    <CheckCircle size={8} />{m.replace(/_/g, ' ')}
                                </span>
                            ))}
                            {data.modules.length > 14 && <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ color: 'var(--muted)' }}>+{data.modules.length - 14}</span>}
                        </div>
                    </div>
                )}

                {/* Blocked Modules */}
                {data.blocked_modules && data.blocked_modules.length > 0 && (
                    <div className="mt-3">
                        <p className="text-[10px] mb-2 font-semibold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>Bloqueados no seu plano</p>
                        <div className="flex gap-1.5 flex-wrap">
                            {data.blocked_modules.map(m => (
                                <span key={m} className="text-[10px] px-2 py-0.5 rounded-full capitalize flex items-center gap-1" style={{ background: 'rgba(239,68,68,0.08)', color: '#EF4444' }}>
                                    <Lock size={8} />{m.replace(/_/g, ' ')}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Add-ons Section */}
            {(addonDetails.length > 0 || availableAddons.length > 0) && (
                <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <div className="flex items-center gap-2 mb-3">
                        <Package size={14} style={{ color: 'var(--sv-gold-primary)' }} />
                        <p className="text-xs font-semibold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Add-ons & Módulos Premium</p>
                    </div>

                    {/* Active add-ons */}
                    {addonDetails.length > 0 && (
                        <div className="mb-3">
                            <p className="text-[10px] mb-1.5 font-semibold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>Ativos</p>
                            <div className="space-y-1.5">
                                {addonDetails.map(a => {
                                    const meta = AVAILABLE_ADDONS_META[a.key] || {};
                                    const AddonIcon = meta.icon || CheckCircle;
                                    const activeInfo = availableAddons.find(x => x.key === a.key);
                                    const qty = activeInfo?.quantity || 0;
                                    return (
                                        <div key={a.key} className="flex items-center gap-2 p-3 rounded-lg" style={{ background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.15)' }} data-testid={`active-addon-${a.key}`}>
                                            <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${meta.color || '#22c55e'}12` }}>
                                                <AddonIcon size={14} style={{ color: meta.color || '#22c55e' }} />
                                            </div>
                                            <div className="flex-1">
                                                <p className="text-[11px] font-semibold" style={{ color: 'var(--text)' }}>{a.name}</p>
                                                <p className="text-[9px]" style={{ color: 'var(--muted)' }}>
                                                    {a.description}
                                                    {a.key === 'usuarios_extras' && qty > 0 && ` — ${qty} usuario(s) extra(s)`}
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: 'rgba(34,197,94,0.12)', color: '#22c55e' }}>Ativo</span>
                                                <button
                                                    onClick={() => handleCancelAddon(a.key)}
                                                    disabled={cancellingAddon === a.key}
                                                    className="p-1 rounded-md transition-colors hover:bg-red-500/10"
                                                    title="Cancelar add-on"
                                                    data-testid={`cancel-addon-${a.key}`}
                                                >
                                                    {cancellingAddon === a.key ? <Loader2 size={12} className="animate-spin" style={{ color: '#EF4444' }} /> : <XCircle size={12} style={{ color: '#EF4444' }} />}
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Available add-ons (not yet active) */}
                    {availableAddons.filter(a => !a.active).length > 0 && (
                        <div>
                            <p className="text-[10px] mb-1.5 font-semibold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>Disponíveis para seu plano</p>
                            <div className="space-y-2">
                                {availableAddons.filter(a => !a.active).map(a => {
                                    const meta = AVAILABLE_ADDONS_META[a.key] || {};
                                    const AddonIcon = meta.icon || Plus;
                                    const isPerUnit = a.type === 'per_user';
                                    const qty = addonQuantities[a.key] || 1;
                                    const totalPrice = isPerUnit ? (a.price * qty) : a.price;
                                    const canBuy = a.has_stripe_price;

                                    return (
                                        <div key={a.key} className="p-3 rounded-lg" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }} data-testid={`addon-${a.key}`}>
                                            <div className="flex items-center gap-2 mb-2">
                                                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${meta.color || 'var(--muted)'}12` }}>
                                                    <AddonIcon size={14} style={{ color: meta.color || 'var(--muted)' }} />
                                                </div>
                                                <div className="flex-1">
                                                    <p className="text-[11px] font-semibold" style={{ color: 'var(--text)' }}>{a.name}</p>
                                                    <p className="text-[9px]" style={{ color: 'var(--muted)' }}>{a.description}</p>
                                                </div>
                                            </div>

                                            <div className="flex items-center justify-between gap-2">
                                                {isPerUnit ? (
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex items-center rounded-lg overflow-hidden" style={{ border: '1px solid var(--border)' }}>
                                                            <button onClick={() => updateAddonQty(a.key, -1)} className="px-2 py-1 transition-colors hover:bg-white/5" data-testid={`addon-qty-minus-${a.key}`}>
                                                                <Minus size={10} style={{ color: 'var(--muted)' }} />
                                                            </button>
                                                            <span className="px-2 py-1 text-[11px] font-bold min-w-[28px] text-center" style={{ color: 'var(--text)', background: 'var(--surface)' }} data-testid={`addon-qty-${a.key}`}>{qty}</span>
                                                            <button onClick={() => updateAddonQty(a.key, 1)} className="px-2 py-1 transition-colors hover:bg-white/5" data-testid={`addon-qty-plus-${a.key}`}>
                                                                <Plus size={10} style={{ color: 'var(--muted)' }} />
                                                            </button>
                                                        </div>
                                                        <span className="text-[10px]" style={{ color: 'var(--muted)' }}>
                                                            R$ {a.price?.toFixed(2)}/un x {qty} = <strong style={{ color: meta.color || 'var(--sv-gold-primary)' }}>R$ {totalPrice.toFixed(2)}/mes</strong>
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span className="text-[11px] font-bold" style={{ color: meta.color || 'var(--sv-gold-primary)' }}>
                                                        R$ {a.price?.toFixed(2)}/mes
                                                    </span>
                                                )}

                                                <button
                                                    onClick={() => handleBuyAddon(a.key)}
                                                    disabled={buyingAddon === a.key || !canBuy}
                                                    className="px-3 py-1.5 rounded-lg text-[10px] font-semibold flex items-center gap-1.5 transition-all hover:scale-[1.02] shrink-0 disabled:opacity-50"
                                                    style={{ background: canBuy ? (meta.color || 'var(--sv-gold-btn)') : 'var(--surface-2)', color: canBuy ? '#0A0A0A' : 'var(--muted)' }}
                                                    data-testid={`buy-addon-${a.key}`}
                                                >
                                                    {buyingAddon === a.key ? <Loader2 size={10} className="animate-spin" /> : <ShoppingCart size={10} />}
                                                    {canBuy ? 'Contratar' : 'Em breve'}
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Auto-renew toggle */}
            {currentPlan !== 'free' && !isTrial && data.plan_status === 'active' && (
                <div className="rounded-xl p-4 flex items-center gap-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <RefreshCw size={16} style={{ color: autoRenew ? '#10B981' : 'var(--muted)' }} />
                    <div className="flex-1">
                        <p className="text-xs font-semibold" style={{ color: 'var(--text)' }}>Renovação automática</p>
                        <p className="text-[10px]" style={{ color: 'var(--muted)' }}>
                            {autoRenew ? 'Seu plano será renovado automaticamente a cada 30 dias.' : 'Desativada. Seu plano expirará na data de vencimento.'}
                        </p>
                    </div>
                    <button onClick={toggleAutoRenew} className="px-3 py-1.5 rounded-lg text-[10px] font-semibold" style={{ background: autoRenew ? 'rgba(16,185,129,0.12)' : 'var(--surface-2)', color: autoRenew ? '#10B981' : 'var(--muted)', border: `1px solid ${autoRenew ? 'rgba(16,185,129,0.25)' : 'var(--border)'}` }} data-testid="auto-renew-toggle">
                        {autoRenew ? 'Ativada' : 'Desativada'}
                    </button>
                </div>
            )}

            {/* CTA */}
            {currentPlan !== 'pro' && currentPlan !== 'enterprise' && (
                <div className="rounded-xl p-4 flex items-center gap-3" style={{ background: 'rgba(var(--sv-accent-rgb), 0.06)', border: '1px solid rgba(var(--sv-accent-rgb), 0.18)' }}>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(var(--sv-accent-rgb), 0.12)' }}>
                        <ArrowUpRight size={18} style={{ color: 'var(--sv-gold-primary)' }} />
                    </div>
                    <div className="flex-1">
                        <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{isTrial ? 'Não perca acesso!' : 'Quer mais recursos?'}</p>
                        <p className="text-[11px]" style={{ color: 'var(--muted)' }}>
                            {currentPlan === 'free' && 'Comece com o Lite por R$ 9,90/mes (logo, ponto inicial, financeiro básico) ou desbloqueie o Start por R$ 19,90/mes.'}
                            {currentPlan === 'lite' && 'Faca upgrade para o Start por R$ 19,90/mes: Ponto Digital funcional, gestor, histórico completo e mais.'}
                            {currentPlan === 'start' && (isTrial ? 'Assine o Start por R$ 19,90/mes para continuar com todos os recursos.' : 'Upgrade para Gestão: Performance, Financeiro completo, Radar de Ofertas, até 10 colaboradores.')}
                            {currentPlan === 'gestão' && 'Upgrade para Pro: Permissões avançadas, Auditoria completa, Radar premium, 25 colaboradores.'}
                        </p>
                    </div>
                    <button onClick={() => setShowCompare(true)} className="px-4 py-2 rounded-xl text-xs font-semibold shrink-0" style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="upgrade-cta-btn">
                        {isTrial ? 'Assinar agora' : 'Fazer Upgrade'}
                    </button>
                </div>
            )}

            {/* Toggle comparativo */}
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Comparativo de Planos</h3>
                <button onClick={() => setShowCompare(!showCompare)} className="text-xs px-3 py-1 rounded-lg" style={{ background: 'var(--surface-2)', color: 'var(--muted)', border: '1px solid var(--border)' }}>
                    {showCompare ? 'Recolher' : 'Ver todos'}
                </button>
            </div>

            {/* Plans grid */}
            <div className={`grid grid-cols-1 ${showCompare ? 'sm:grid-cols-2 lg:grid-cols-3' : 'sm:grid-cols-2'} gap-3`}>
                {allPlans.filter(p => showCompare || PLAN_ORDER.indexOf(p.slug) > currentIdx || (isTrial && p.slug === currentPlan)).map((p) => {
                    const meta = PLAN_META[p.slug] || PLAN_META.free;
                    const PIcon = meta.icon;
                    const isCurrent = p.slug === currentPlan;
                    const isUpgrade = PLAN_ORDER.indexOf(p.slug) > currentIdx || (isTrial && p.slug === currentPlan);
                    const canBuy = p.slug !== 'free' && p.slug !== 'enterprise' && isUpgrade;
                    const rp = p.radar_priority || 0;
                    const isRecommended = !isCurrent && isUpgrade && PLAN_ORDER.indexOf(p.slug) === currentIdx + 1;

                    return (
                        <div key={p.slug} className="rounded-xl p-4 transition-all relative" style={{
                            background: isCurrent ? meta.gradient : isRecommended ? `${meta.gradient}` : 'var(--surface)',
                            border: isRecommended ? `2px solid ${meta.color}` : isCurrent ? `1px solid ${meta.color}40` : '1px solid var(--border)',
                            boxShadow: isRecommended ? `0 0 20px ${meta.color}15` : 'none',
                        }} data-testid={`plan-card-${p.slug}`}>
                            {/* Badges */}
                            <div className="absolute top-2 right-2 flex gap-1">
                                {isCurrent && !isTrial && <span className="text-[9px] px-2 py-0.5 rounded-full font-bold" style={{ background: `${meta.color}15`, color: meta.color }}>SEU PLANO</span>}
                                {isCurrent && isTrial && <span className="text-[9px] px-2 py-0.5 rounded-full font-bold" style={{ background: 'rgba(245,158,11,0.15)', color: '#F59E0B' }}>TRIAL</span>}
                                {isRecommended && <span className="text-[9px] px-2 py-0.5 rounded-full font-bold animate-pulse" style={{ background: `${meta.color}20`, color: meta.color }}>RECOMENDADO</span>}
                            </div>
                            {/* Header */}
                            <div className="flex items-center gap-2 mb-1 mt-1">
                                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${meta.color}12` }}>
                                    <PIcon size={16} style={{ color: meta.color }} />
                                </div>
                                <span className="text-sm font-bold" style={{ color: 'var(--text)' }}>{p.name}</span>
                            </div>
                            {/* Price */}
                            <div className="mb-3">
                                <span className="text-xl font-bold" style={{ color: meta.color, fontFamily: "var(--font-display)" }}>
                                    {p.price > 0 ? `R$ ${p.price.toFixed(2)}` : p.slug === 'enterprise' ? 'Sob consulta' : 'Gratis'}
                                </span>
                                {p.price > 0 && <span className="text-[10px] ml-1" style={{ color: 'var(--muted)' }}>/mes</span>}
                            </div>
                            {/* Limits row */}
                            <div className="flex gap-2 mb-3 text-[10px]">
                                <span className="flex items-center gap-1 px-2 py-1 rounded-md" style={{ background: 'var(--surface-2)', color: 'var(--muted)' }}>
                                    <Users size={9} /> {p.max_employees >= 9999 ? '∞' : p.max_employees}
                                </span>
                                <span className="flex items-center gap-1 px-2 py-1 rounded-md" style={{ background: 'var(--surface-2)', color: 'var(--muted)' }}>
                                    <Shield size={9} /> {p.max_gestores >= 9999 ? '∞' : p.max_gestores} gestor
                                </span>
                                <span className="flex items-center gap-1 px-2 py-1 rounded-md" style={{ background: `${RADAR_COLORS[rp]}10`, color: RADAR_COLORS[rp] }}>
                                    <Radar size={9} /> {RADAR_LABELS[rp]}
                                </span>
                            </div>
                            {/* Features */}
                            {p.features && (
                                <div className="space-y-1.5 mb-3">
                                    {p.features.slice(0, showCompare ? 20 : 5).map((f, i) => (
                                        <div key={i} className="flex items-start gap-1.5">
                                            <CheckCircle size={9} className="shrink-0 mt-0.5" style={{ color: meta.color }} />
                                            <span className="text-[10px]" style={{ color: 'var(--muted)' }}>{f}</span>
                                        </div>
                                    ))}
                                    {!showCompare && p.features.length > 5 && (
                                        <span className="text-[9px] font-medium" style={{ color: meta.color }}>+{p.features.length - 5} recursos</span>
                                    )}
                                </div>
                            )}
                            {/* CTA */}
                            {canBuy && (
                                <button onClick={() => handleUpgrade(p.slug)} disabled={upgradingPlan === p.slug}
                                    className="w-full py-2.5 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-all hover:scale-[1.02]"
                                    style={{ background: meta.color, color: '#0A0A0A' }} data-testid={`buy-plan-${p.slug}`}>
                                    {upgradingPlan === p.slug ? <Loader2 size={12} className="animate-spin" /> : <CreditCard size={12} />}
                                    {isTrial && isCurrent ? 'Assinar agora' : `Assinar ${p.name}`}
                                </button>
                            )}
                            {isCurrent && !canBuy && !isTrial && (
                                <div className="w-full py-2 rounded-lg text-xs text-center" style={{ background: 'var(--surface-2)', color: 'var(--muted)' }}>Plano atual</div>
                            )}
                            {!isCurrent && !canBuy && p.slug !== 'enterprise' && (
                                <div className="w-full py-2 rounded-lg text-xs text-center" style={{ background: 'var(--surface-2)', color: 'var(--disabled)' }}>Plano inferior</div>
                            )}
                            {p.slug === 'enterprise' && !isCurrent && <p className="text-[10px] text-center py-2 rounded-lg mt-1" style={{ background: `${meta.color}08`, color: meta.color }}>Contate o administrador</p>}
                        </div>
                    );
                })}
            </div>

            {/* Transaction History */}
            <div className="rounded-xl overflow-hidden" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <button onClick={fetchTxHistory} className="w-full flex items-center justify-between p-4" data-testid="tx-history-toggle">
                    <div className="flex items-center gap-2">
                        <Receipt size={14} style={{ color: 'var(--sv-gold-primary)' }} />
                        <p className="text-xs font-semibold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Historico de Cobrancas</p>
                    </div>
                    {loadingHistory ? <Loader2 size={14} className="animate-spin" style={{ color: 'var(--muted)' }} /> :
                        showHistory ? <ChevronUp size={14} style={{ color: 'var(--muted)' }} /> : <ChevronDown size={14} style={{ color: 'var(--muted)' }} />}
                </button>
                {showHistory && (
                    <div className="px-4 pb-4" data-testid="tx-history-list">
                        {txHistory.length === 0 ? (
                            <p className="text-[10px] py-3 text-center" style={{ color: 'var(--muted)' }}>Nenhuma cobranca encontrada.</p>
                        ) : (
                            <div className="space-y-1.5">
                                {txHistory.map((tx, i) => {
                                    const isAddon = tx.checkout_type === 'addon';
                                    const statusColor = tx.payment_status === 'paid' ? '#22c55e' : tx.payment_status === 'pending' ? '#F59E0B' : '#EF4444';
                                    const statusLabel = tx.payment_status === 'paid' ? 'Pago' : tx.payment_status === 'pending' ? 'Pendente' : tx.payment_status === 'payment_failed' ? 'Falhou' : tx.payment_status;
                                    return (
                                        <div key={tx.tx_id || i} className="flex items-center gap-3 p-2.5 rounded-lg" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }} data-testid={`tx-row-${i}`}>
                                            <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: isAddon ? 'rgba(139,92,246,0.1)' : 'rgba(var(--sv-accent-rgb), 0.1)' }}>
                                                {isAddon ? <Package size={12} style={{ color: '#8B5CF6' }} /> : <CreditCard size={12} style={{ color: 'var(--sv-accent)' }} />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-[11px] font-medium truncate" style={{ color: 'var(--text)' }}>
                                                    {isAddon ? `Add-on: ${tx.addon_key || ''}` : `Plano: ${tx.plan || ''}`}
                                                    {tx.quantity > 1 && <span style={{ color: 'var(--muted)' }}> x{tx.quantity}</span>}
                                                </p>
                                                <p className="text-[9px]" style={{ color: 'var(--muted)' }}>
                                                    {tx.created_at ? new Date(tx.created_at).toLocaleDateString('pt-BR') : '—'}
                                                    {tx.session_id && <span> | {tx.session_id.substring(0, 16)}...</span>}
                                                </p>
                                            </div>
                                            <div className="text-right shrink-0">
                                                {tx.amount > 0 && <p className="text-[11px] font-bold" style={{ color: 'var(--text)' }}>R$ {tx.amount?.toFixed(2)}</p>}
                                                <span className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold" style={{ background: `${statusColor}15`, color: statusColor }}>{statusLabel}</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="rounded-xl p-4" style={{ background: 'rgba(var(--sv-accent-rgb), 0.05)', border: '1px solid rgba(var(--sv-accent-rgb), 0.15)' }}>
                <p className="text-xs" style={{ color: 'var(--sv-gold-primary)', lineHeight: 1.6 }}>
                    Pagamento seguro via Stripe. Ao assinar, seu plano sera ativado imediatamente.
                    {data.payment_ref && <><br/>Referência: <strong>{data.payment_ref}</strong></>}
                </p>
            </div>
        </div>
    );
}
